import { ArrowBack, CancelOutlined, FileUploadOutlined, Save, Send } from '@mui/icons-material';
import { Box, Button, Checkbox, Divider, FormControl, FormControlLabel, Grid, IconButton, LinearProgress, Radio, RadioGroup, TextField, Typography } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify';
import { ApiGetChecklistPdf, ApiGetChecklistSubmissionQuestions, ApiSubmitVenueChecklist, ApiuploadDocuments } from '../../apiService/Core/Apicall';
import storage from '../../utils/storage';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik } from 'formik';


type QuestionAnswer = {
    answerValue: string;
    comment: string;
    photoUrls: string[];
    isPass: boolean | null;

    // validation
    isCommentRequired: boolean;
    minCommentChar: number;
    optionCommentType: string,
    optionCommentLabel: string,

    isPhotoRequired: boolean;
    minPhoto: number;
};

function VenueCheklistForm() {
    const [questionList, setQuestionList] = useState<any>([])
    const [checklistResponse, setChecklistResponse] = useState<any>([])
    const [questionAnswers, setQuestionAnswers] = useState<Record<number, QuestionAnswer>>([]);
    const [validationErrors, setValidationErrors] = useState<
        Record<
            number,
            {
                answer?: string;
                comment?: string;
                photo?: string;
            }
        >
    >({});
    const questionRefs = useRef<Record<number, HTMLDivElement | null>>({});
    const [progress, setProgress] = useState({
        totalQuestions: 0,
        answeredQuestions: 0,
        percentage: 0,
    });
    const { businessCheckListSubmissionId } = useParams();
    const navigate = useNavigate()
    const [isClient, setIsClient] = useState(storage.getItem('userRole') === 'clientadmin' || storage.getItem('userRole') === 'areaManager' || checklistResponse?.status === "SUBMITTED")

    useEffect(() => {
        setIsClient(checklistResponse?.status === "SUBMITTED")
    }, [checklistResponse])

    const fileupload = async (filePath: File, fileType: string, answerId: number) => {
        const userData = JSON.parse(storage.getItem("userData") || "{}");
        const userId = userData?.userId;

        const formData = new FormData();
        formData.append("userId", userId);
        formData.append("fileType", fileType);
        formData.append("file", filePath);

        try {
            const response: any = await ApiuploadDocuments(formData);
            if (response.message) {
                setValidationErrors(prev => ({
                    ...prev,
                    [answerId]: {
                        ...prev[answerId],
                        photo: undefined
                    }
                }));

                setQuestionAnswers(prev => ({

                    ...prev,

                    [answerId]: {

                        ...prev[answerId],

                        photoUrls: [
                            ...(prev[answerId]?.photoUrls || []),
                            response.location
                        ]
                    }

                }));
            }
        } catch (error) {
            console.error('Upload failed:', error);
            toast.error("Failed to upload image");
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, answerId: number) => {
        const files = event.target.files;
        if (!files) return;

        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            if (file.type.startsWith("image/")) {
                fileupload(file, "profile_pic", answerId);
            } else {
                toast.error("Please upload a valid image file.");
            }
        }
        event.target.value = '';
    };

    const handleRemoveImage = (
        answerId: number,
        index: number
    ) => {

        setQuestionAnswers(prev => {

            const images = [...(prev[answerId]?.photoUrls || [])];

            images.splice(index, 1);

            return {

                ...prev,

                [answerId]: {

                    ...prev[answerId],

                    photoUrls: images
                }
            }

        });

    };

    const updateAnswer = (
        answerId: number,
        field: keyof QuestionAnswer,
        value: any
    ) => {
        setQuestionAnswers(prev => ({
            ...prev,
            [answerId]: {
                ...(prev[answerId] ?? {
                    answerValue: "",
                    comment: "",
                    photoUrls: [],
                    isPass: null,
                    isCommentRequired: false,
                    minCommentChar: 0,
                    isPhotoRequired: false,
                    minPhoto: 0,
                }),
                [field]: value,
            },
        }));
    };

    const validateQuestions = () => {
        const errors: Record<number, any> = {};
        let firstInvalidId: number | null = null;

        for (const category of questionList) {
            for (const question of category.questions) {
                const answerId = question.businessCheckListAnswerId;
                const answer = questionAnswers[answerId];

                if (!answer) continue;

                const questionError: any = {};

                // Answer validation
                if (
                    answer.answerValue === undefined ||
                    answer.answerValue === null ||
                    answer.answerValue === "" ||
                    (Array.isArray(answer.answerValue) &&
                        answer.answerValue.length === 0)
                ) {
                    questionError.answer = "This question is required";
                }

                // Comment validation
                if (
                    answer.isCommentRequired &&
                    answer.comment.trim().length < answer.minCommentChar
                ) {
                    questionError.comment = `Minimum ${answer.minCommentChar} characters required`;
                }

                // Photo validation
                if (
                    answer.isPhotoRequired &&
                    answer.photoUrls.length < answer.minPhoto
                ) {
                    questionError.photo = `Please upload minimum ${answer.minPhoto} photos`;
                }

                if (Object.keys(questionError).length > 0) {
                    errors[answerId] = questionError;

                    if (firstInvalidId === null) {
                        firstInvalidId = answerId;
                    }
                }
            }
        }

        setValidationErrors(errors);

        if (firstInvalidId !== null) {
            const element = questionRefs.current[firstInvalidId];

            if (element) {
                element.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });

                setTimeout(() => {
                    const input = element.querySelector(
                        "input, textarea, select, button"
                    ) as HTMLElement | null;

                    input?.focus();
                }, 300);
            }

            return false;
        }

        return true;
    };

    const submit = async (isFinalSubmit?: boolean) => {
        if (isFinalSubmit) {
            if (!validateQuestions()) return;
        }

        const payload = {
            businessCheckListSubmissionId: businessCheckListSubmissionId,
            isNeedToSubmit: isFinalSubmit,
            questions: Object.entries(questionAnswers).map(([id, value]) => ({
                businessCheckListAnswerId: Number(id),
                answerValue: value.answerValue,
                comment: value.comment,
                photoUrls: value.photoUrls.join(","),
                isPass: value.isPass
            }))
        };
        try {
            const response = await ApiSubmitVenueChecklist(payload)
            if (response?.status || response?.success) {
                navigate(-1)
            }
        } catch (error: any) {
            console.log("Error", error)
        }
        console.log(payload);
    };

    const renderQuestion = (q: any, index: number, categoryName: string) => {
        const answerId = q.businessCheckListAnswerId;
        const questionId = q.checklistQuestionId;
        return (
            <Box key={questionId}
                ref={(el: any) => {
                    questionRefs.current[answerId] = el;
                }}
                className='bg-light-main p-4 text-dark border-20 mb-3'>
                <Box className='d-flex align-items-center gap-2 mb-3'>
                    <Typography className='text-blue border-blue border-10 px-3 py-1 bg-light-blue'>{categoryName}</Typography>
                    <Typography variant='body2'>Question {index} out of {progress?.totalQuestions}</Typography>
                </Box>
                <Typography className='fw-bold' gutterBottom variant='h6'>{q.question}</Typography>
                {q.questionDescription && (
                    <Typography
                        className="mt-2"
                        variant='body1'
                        dangerouslySetInnerHTML={{
                            __html: q.questionDescription,
                        }}
                    />
                )}

                {q.questionHint && (
                    <Box className='border-10 border p-2' sx={{bgcolor:'#FFFFFF'}}>
                    <Typography className="mt-2" variant='body1'>
                        <strong>Hint :</strong>{" "}
                        <span
                            dangerouslySetInnerHTML={{
                                __html: q.questionHint,
                            }}
                        />
                    </Typography>
                    </Box>
                )}

                {q.questionHintImages && (
                    <Grid container spacing={2} className="my-3">
                        {q.questionHintImages
                            .split(",")
                            .map((url: string, idx: number) => (
                                <Grid size={'auto'} key={idx}>
                                    <Box
                                        component="img"
                                        src={url.trim()}
                                        width="100%"
                                        height={150}
                                        sx={{
                                            objectFit: "cover",
                                            borderRadius: 2,
                                        }}
                                    />
                                </Grid>
                            ))}
                    </Grid>
                )}

                {/* RADIO */}
                <Box  className='my-2' sx={{border:'1px dashed var(--primary-color)'}} />
                {q.qType === "R" && (
                    <RadioGroup
                        value={
                            questionAnswers[answerId]?.answerValue || ""
                        }
                        onChange={(e) => {
                            setValidationErrors(prev => ({
                                ...prev,
                                [answerId]: {
                                    ...prev[answerId],
                                    answer: undefined
                                }
                            }));

                            const option = q.qOptionJson.find(
                                (x: any) => x.value === e.target.value
                            );

                            updateAnswer(answerId, "answerValue", option.value);
                            updateAnswer(answerId, "isPass", option.isPass);
                            updateAnswer(
                                answerId,
                                "isCommentRequired",
                                option?.optionComment === "yes"
                            );

                            updateAnswer(
                                answerId,
                                "optionCommentType",
                                option?.optionCommentType
                            );

                            updateAnswer(
                                answerId,
                                "minCommentChar",
                                option.minChar || 0
                            );

                            updateAnswer(
                                answerId,
                                "isPhotoRequired",
                                option.optionImage > 0
                            );

                            updateAnswer(
                                answerId,
                                "minPhoto",
                                option.optionImage || 0
                            );

                        }}
                    >
                        {/* {q.qOptionJson.map((option: any) => (
                            <FormControlLabel
                                key={option.optionId}
                                value={option.value}
                                control={<Radio />}
                                label={option.value}
                            />
                        ))} */}
                        {q.qOptionJson.map((option: any) => {
                            const selected =
                                questionAnswers[answerId]?.answerValue === option.value;

                            return (
                                <Box
                                    key={option.optionId}
                                    sx={{
                                        border: "1px solid",
                                        borderColor: selected
                                            ? option?.isPass === true
                                                ? "var(--primary-color)"
                                                : option?.isPass === false
                                                    ? "#D52E2E"
                                                    : "warning.main"
                                            : "#FFFFFF",
                                        bgcolor: selected
                                            ? option?.isPass === true
                                                ? "var(--primary-light-main-color)"
                                                : option?.isPass === false
                                                    ? "#D52E2E1A"
                                                    : "#ed6c021A"
                                            : "#FFF",
                                        borderRadius: "20px",
                                        mb: 2,
                                        px: 1,
                                        py: 1,
                                        transition: "0.2s",
                                    }}
                                >
                                    <FormControlLabel
                                        value={option.value}
                                        sx={{
                                            width: "100%",
                                            margin: 0,
                                            ".MuiFormControlLabel-label": {
                                                width: "100%",
                                            },
                                        }}
                                        control={
                                            <Radio
                                                disabled={isClient}
                                                sx={{
                                                    color: "#D7C9B8",
                                                    "&.Mui-checked": {
                                                        color: option.isPass == true ? "var(--primary-color)" : option.isPass == false ? "#D52E2E" : "warning.main",
                                                    },
                                                }}
                                            />
                                        }
                                        label={
                                            <Box
                                                display="flex"
                                                justifyContent="space-between"
                                                alignItems="center"
                                                width="100%"
                                            >
                                                <Typography fontSize={16}>
                                                    {option.value}
                                                </Typography>

                                                <Typography
                                                    fontWeight={700}
                                                    fontSize={13}
                                                    className='me-2'
                                                    color={
                                                        option.isPass == true
                                                            ? "var(--primary-color)"
                                                            : option?.isPass == false ? "#D52E2E" : "warning"
                                                    }
                                                >
                                                    {option.isPass == true ? "PASS" : option.isPass == false ? "FAIL" : "WARN"}
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                </Box>
                            );
                        })}
                    </RadioGroup>
                )}

                {/* CHECKBOX */}

                {q.qType === "C" && (
                    <Box>
                        {q.qOptionJson.map((option: any) => {
                            const selected =
                                (
                                    questionAnswers[
                                        answerId
                                    ]?.answerValue || ""
                                )
                                    .split(",")
                                    .filter(Boolean);

                            return (
                                <FormControlLabel
                                    key={option.optionId}
                                    control={
                                        <Checkbox
                                            disabled={isClient}
                                            checked={selected.includes(option.value)}
                                            onChange={(e) => {
                                                setValidationErrors(prev => ({
                                                    ...prev,
                                                    [answerId]: {
                                                        ...prev[answerId],
                                                        answer: undefined
                                                    }
                                                }));
                                                let values = [...selected];

                                                if (e.target.checked) {
                                                    values.push(option.value);
                                                } else {
                                                    values = values.filter(v => v !== option.value);
                                                }

                                                // Save selected values
                                                updateAnswer(
                                                    answerId,
                                                    "answerValue",
                                                    values.join(",")
                                                );

                                                // Calculate pass
                                                const pass =
                                                    values.length === 0
                                                        ? null
                                                        : values.every(v =>
                                                            q.qOptionJson.find(
                                                                (o: any) => o.value === v
                                                            )?.isPass
                                                        );

                                                updateAnswer(answerId, "isPass", pass);

                                                // ==========================
                                                // ADD THIS CODE HERE
                                                // ==========================

                                                const selectedOptions = q.qOptionJson.filter((o: any) =>
                                                    values.includes(o.value)
                                                );

                                                const requireComment = selectedOptions.some(
                                                    (o: any) =>
                                                        o.optionComment?.toLowerCase() === "yes"
                                                );

                                                const minComment = Math.max(
                                                    ...selectedOptions.map(
                                                        (o: any) => o.minChar || 0
                                                    ),
                                                    0
                                                );

                                                updateAnswer(
                                                    answerId,
                                                    "isCommentRequired",
                                                    requireComment
                                                );
                                                updateAnswer(
                                                    answerId,
                                                    "optionCommentType",
                                                    option?.optionCommentType
                                                );

                                                updateAnswer(
                                                    answerId,
                                                    "minCommentChar",
                                                    minComment
                                                );

                                                const requirePhoto = selectedOptions.some(
                                                    (o: any) => o.optionImage > 0
                                                );

                                                const minPhoto = Math.max(
                                                    ...selectedOptions.map(
                                                        (o: any) => o.optionImage || 0
                                                    ),
                                                    0
                                                );

                                                updateAnswer(
                                                    answerId,
                                                    "isPhotoRequired",
                                                    requirePhoto
                                                );

                                                updateAnswer(
                                                    answerId,
                                                    "minPhoto",
                                                    minPhoto
                                                );
                                            }}
                                        />
                                    }
                                    label={option.value}
                                />
                            );
                        })}
                    </Box>
                )}

                {/* TEXT */}

                {q.qType === "T" && (
                    <>
                        <FormControl fullWidth className="mt-3">
                            <Typography mb={1}>Answer</Typography>

                            <TextField
                                multiline
                                rows={3}
                                disabled={isClient}
                                value={
                                    questionAnswers[answerId]
                                        ?.answerValue || ""
                                }
                                className='bg-white border-10 border-none m-0 p-0'
                                onChange={(e) => {
                                    updateAnswer(answerId, "answerValue", e.target.value);

                                    updateAnswer(
                                        answerId,
                                        "isCommentRequired",
                                        q.isCommentBox === 1
                                    );

                                    updateAnswer(
                                        answerId,
                                        "minCommentChar",
                                        q.minChar
                                    );

                                    updateAnswer(
                                        answerId,
                                        "isPhotoRequired",
                                        q.isPhotoRequire === 1
                                    );

                                    updateAnswer(
                                        answerId,
                                        "minPhoto",
                                        q.minPhoto
                                    );
                                    setValidationErrors(prev => ({
                                        ...prev,
                                        [answerId]: {
                                            ...prev[answerId],
                                            answer: undefined
                                        }
                                    }));
                                }}
                            />
                        </FormControl>
                        {/* <FormControlLabel
                            sx={{ mt: 1 }}
                            control={
                                <Checkbox
                                    disabled={isClient}
                                    checked={questionAnswers[answerId]?.isPass ?? false}
                                    onChange={(e) =>
                                        updateAnswer(answerId, "isPass", e.target.checked)
                                    }
                                />
                            }
                            label="Pass ?"
                        /> */}
                        <FormControl sx={{ mt: 1 }}>
                            <RadioGroup
                                row
                                value={
                                    questionAnswers[answerId]?.isPass === true
                                        ? "pass"
                                        : questionAnswers[answerId]?.isPass === false
                                            ? "fail"
                                            : "warn"
                                }
                                onChange={(e) => {
                                    const value = e.target.value;

                                    updateAnswer(
                                        answerId,
                                        "isPass",
                                        value === "pass"
                                            ? true
                                            : value === "fail"
                                                ? false
                                                : null
                                    );
                                }}
                            >
                                <FormControlLabel
                                    value="pass"
                                    control={<Radio color="success" />}
                                    label="Pass"
                                    disabled={isClient}
                                />

                                <FormControlLabel
                                    value="fail"
                                    control={<Radio color="error" />}
                                    label="Fail"
                                    disabled={isClient}
                                />

                                <FormControlLabel
                                    value="warn"
                                    control={<Radio color="warning" />}
                                    label="Warn"
                                    disabled={isClient}
                                />
                            </RadioGroup>
                        </FormControl>
                    </>
                )
                }

                {/* COMMENT */}

                {
                    questionAnswers[answerId]?.isCommentRequired && questionAnswers[answerId]?.optionCommentType == 'text' && (
                        <FormControl fullWidth className="mt-3">

                            <Typography mb={1}>
                                Comment
                                {questionAnswers[answerId]?.minCommentChar > 0 &&
                                    ` (Minimum ${questionAnswers[answerId]?.minCommentChar} characters)`
                                }
                            </Typography>

                            <TextField
                                multiline
                                disabled={isClient}
                                rows={3}
                                label={questionAnswers[answerId]?.optionCommentLabel}
                                value={questionAnswers[answerId]?.comment || ""}
                                onChange={(e) => {
                                    updateAnswer(
                                        answerId,
                                        "comment",
                                        e.target.value
                                    )
                                    setValidationErrors(prev => ({
                                        ...prev,
                                        [answerId]: {
                                            ...prev[answerId],
                                            comment: undefined
                                        }
                                    }));
                                }}
                            />
                            {validationErrors[answerId]?.comment && (
                                <Typography
                                    color="error"
                                    variant="caption"
                                >
                                    {validationErrors[answerId].comment}
                                </Typography>
                            )}
                        </FormControl>
                    )
                }

                {/* IMAGE UPLOAD */}

                {
                    questionAnswers[answerId]?.isPhotoRequired &&
                    (

                        <FormControl fullWidth className="mt-3">
                            <Typography variant="caption">
                                Upload minimum {questionAnswers[answerId]?.minPhoto} photos
                            </Typography>
                            <Grid container spacing={2} alignItems="center">
                                <Grid size={'auto'}>
                                    <label htmlFor={`upload-${answerId}`}>
                                        <input
                                            disabled={isClient}
                                            hidden
                                            id={`upload-${answerId}`}
                                            type="file"
                                            multiple
                                            accept="image/*"
                                            onChange={(e) =>
                                                handleFileChange(e, answerId)
                                            }
                                        />

                                        <Box
                                            sx={{
                                                height: 150,
                                                padding: '30px',
                                                border: "1px dashed grey",
                                                borderRadius: 2,
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                cursor: "pointer",
                                            }}
                                        >
                                            <Box textAlign="center" className='d-flex flex-column align-items-center justify-content-center'>
                                                <FileUploadOutlined />
                                                <Typography variant="caption">
                                                    Upload Images
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </label>
                                </Grid>
                                {(questionAnswers[answerId]?.photoUrls || []).map(
                                    (img: string, imgIndex: number) => (
                                        <Grid size={'auto'} key={imgIndex}>
                                            <Box
                                                sx={{
                                                    position: "relative",
                                                    height: 150,
                                                    border: "1px solid #ddd",
                                                    borderRadius: 2,
                                                    overflow: "hidden",
                                                }}
                                            >
                                                <Box
                                                    component="img"
                                                    src={img}
                                                    width="100%"
                                                    height="100%"
                                                    sx={{
                                                        objectFit: "cover",
                                                    }}
                                                />

                                                <IconButton
                                                    sx={{
                                                        position: "absolute",
                                                        top: 5,
                                                        right: 5,
                                                        bgcolor: "#fff",
                                                    }}
                                                    onClick={() =>
                                                        handleRemoveImage(
                                                            answerId,
                                                            imgIndex
                                                        )
                                                    }
                                                >
                                                    <CancelOutlined color="error" />
                                                </IconButton>
                                            </Box>
                                        </Grid>
                                    )
                                )}
                            </Grid>
                            {validationErrors[answerId]?.photo && (
                                <Typography
                                    color="error"
                                    variant="caption"
                                >
                                    {validationErrors[answerId].photo}
                                </Typography>
                            )}
                        </FormControl>
                    )
                }
                {
                    validationErrors[answerId]?.answer && (
                        <Typography
                            color="error"
                            variant="caption"
                        >
                            {validationErrors[answerId].answer}
                        </Typography>
                    )
                }
            </Box >
        );
    };


    const getChecklistData = async () => {
        const data = `?businessCheckListSubmissionId=${businessCheckListSubmissionId}`;

        try {
            const res = await ApiGetChecklistSubmissionQuestions(data);

            if (res?.status) {

                const sortedCategories = [...res.data.categories]
                    .sort((a: any, b: any) => a.categoryOrder - b.categoryOrder)
                    .map((category: any) => ({
                        ...category,
                        questions: [...category.questions].sort(
                            (a: any, b: any) => a.displayOrder - b.displayOrder
                        ),
                    }));

                setQuestionList(sortedCategories);
                setChecklistResponse(res?.data)
                // Create initial answer object
                const initialAnswers: Record<number, QuestionAnswer> = {};

                sortedCategories.forEach((category: any) => {
                    category.questions.forEach((question: any) => {
                        const selectedOption = question.qOptionJson?.find(
                            (option: any) => option.value === question.answerValue
                        );

                        const isYesComment =
                            selectedOption?.optionComment?.toLowerCase() === "yes";
                        initialAnswers[question.businessCheckListAnswerId] = {

                            answerValue: question.answerValue || "",

                            comment: question.comment || "",

                            photoUrls: question.photoUrls
                                ? question.photoUrls.split(",").filter(Boolean)
                                : [],

                            isPass: question.isPass,

                            // Default rules for Text questions
                            isCommentRequired:
                                question.qType === "T"
                                    ? question.isCommentBox === 1
                                    : isYesComment,

                            minCommentChar: question.qType === "T"
                                ? question.minChar || 0
                                : isYesComment
                                    ? question.minChar || 0
                                    : 0,

                            optionCommentType:
                                question.qType === "T"
                                    ? "text"
                                    : selectedOption?.optionCommentType || "",

                            optionCommentLabel:
                                question.qType === "T"
                                    ? ""
                                    : selectedOption?.optionCommentLabel || "",

                            isPhotoRequired: question.isPhotoRequire === 1,

                            minPhoto: question.minPhoto || 0

                        };
                    });
                });

                setQuestionAnswers(initialAnswers);
            }
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        getChecklistData()
    }, [])

    let questionIndex = 0;

    useEffect(() => {
        const totalQuestions = questionList.reduce(
            (total: number, category: any) => total + category.questions.length,
            0
        );

        const answeredQuestions = Object.values(questionAnswers).filter(
            (answer: any) => {
                if (Array.isArray(answer.answerValue)) {
                    return answer.answerValue.length > 0;
                }

                return (
                    answer.answerValue !== null &&
                    answer.answerValue !== undefined &&
                    String(answer.answerValue).trim() !== ""
                );
            }
        ).length;

        const percentage =
            totalQuestions > 0
                ? (answeredQuestions / totalQuestions) * 100
                : 0;

        setProgress({
            totalQuestions,
            answeredQuestions,
            percentage,
        });
    }, [questionAnswers, questionList]);

    const downloadChecklist = async () => {
        const checkListType = checklistResponse?.checkListType || checklistResponse?.checkList?.type || storage.getItem('dashboardCheckListType') || "BUSINESS";
        const checkAreaId = checklistResponse?.areaId || checklistResponse?.area?.areaId || storage.getItem('areaId');
        const areaIdParam = (checkListType === "AREA_MANAGER" && checkAreaId && checkAreaId !== "undefined" && checkAreaId !== "null") ? `&areaId=${checkAreaId}` : '';
        const data = `?businessCheckListSubmissionId=${businessCheckListSubmissionId}&clientEntityId=${storage.getItem('clientEntityId')}&checkListType=${checkListType}${areaIdParam}`;

        try {
            const response = await ApiGetChecklistPdf(data);

            const blob = new Blob([response.data], { type: 'application/pdf' });

            if (blob.size === 0) {
                throw new Error("Empty PDF file.");
            }
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${checklistResponse?.businessCheckListTitle}_${checklistResponse?.month}_${checklistResponse?.year}.pdf`; // custom file name
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            // setPdfUrl(url); // this will trigger rendering the PDF
        } catch (error: any) {
            console.log(error)
        }
    };
    return (
        <Box position={'relative'}>
            <Box className='d-flex align-items-center justify-content-between'>
                <Button className='btn-outlined py-1 mb-3 px-2 d-flex align-items-center gap-2' variant='outlined' onClick={() => navigate(-1)}><ArrowBack /> Back</Button>
                {isClient && (<Button className='btn-main py-1 mb-3 px-2 d-flex align-items-center gap-2' onClick={() => downloadChecklist()}>Download as pdf</Button>)}
            </Box>
            <Box
                className='bg-light-main border-20 p-4 mb-3'>
                <Box className='d-flex align-items-center justify-content-between mb-2'>
                    <Typography variant="h5" fontWeight={700}>
                        {checklistResponse?.businessCheckListTitle}
                    </Typography>

                    <Box textAlign="right">
                        <Typography variant="caption">
                            PROGRESS
                        </Typography>

                        <Typography fontWeight={700}>
                            {progress.answeredQuestions}/
                            {progress.totalQuestions} answered
                        </Typography>
                    </Box>
                </Box>

                <LinearProgress
                    variant="determinate"
                    value={progress.percentage}
                    sx={{
                        height: 8,
                        borderRadius: 5,
                        backgroundColor: "#c9c9c9",
                        "& .MuiLinearProgress-bar": {
                            backgroundColor: "var(--primary-color)",
                            borderRadius: 5,
                        },
                    }}
                />
            </Box>
            <Grid container spacing={2}>
                <Grid size={12}>
                    {questionList.map((category: any, index: number) => (
                        <Box key={category.checklistCategoryId} mt={4}>
                            {category.questions.map((question: any, index: number) => {
                                questionIndex++;
                                return (
                                    renderQuestion(question, questionIndex, category?.categoryName)
                                )
                            }
                            )}
                        </Box>
                    ))}
                </Grid>
                {/* <Grid size={3}>

                </Grid> */}
            </Grid>
            {!isClient && (
                <Box className='d-flex align-items-center justify-content-end' gap={2}>
                    <Button className='btn-outlined py-2 px-4 d-flex align-items-center gap-2' variant='outlined' onClick={() => { submit(false) }}><Save />Save Draft</Button>
                    <Button className='btn-main py-2 px-4 d-flex align-items-center gap-2' onClick={() => { submit(true) }}><Send sx={{ rotate: '-45deg' }} />Submit Checklist</Button>
                </Box>
            )}
            {isClient && (
                <Button className='btn-outlined py-1 mb-3 px-2 d-flex align-items-center ms-auto gap-2' variant='outlined' onClick={() => navigate(-1)}><ArrowBack /> Back</Button>
            )}
        </Box>
    )
}

export default VenueCheklistForm
